import {Component, OnDestroy, OnInit} from '@angular/core';
import { VehicleCategoryModel, VehicleGroupMobileConfiguration } from '../../../../../shared/models/vehicle';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {mergeMap, Subscription} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';
import {VehiclesService} from '../../../../../data/vehicles/vehicles.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDivider} from '@angular/material/divider';
import {MatError, MatFormField } from '@angular/material/form-field';
import {RowWithSelectComponent} from '../../row-with-select/row-with-select.component';
import {RowWithToggleComponent} from '../../row-with-toggle/row-with-toggle.component';
import {CardComponent} from '../../../../../shared/components/card/card.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {NamedId} from '../../../../../shared/models/NamedId';
import {RootRoute, SettingsRoute} from '../../../../../shared/models/angular-routing';
import {CardContentDirective} from '../../../../../shared/components/card/card-content.directive';
import {ObservationManagementService} from '../../../../../data/observations/observation-management.service';
import {InspectionFormsService} from '../../../../../data/inspection-forms/inspection-forms.service';
import {RoutesService} from '../../../../../data/routes/routes.service';
import { ConfigurationService } from '../../../../../configuration/configuration.service';
import { MatOption, MatSelect } from '@angular/material/select';
import { BaseMapType, StatusLayerType } from '../../../../../configuration/settings.service';

interface VehicleGroupMobileFormModel {
  showVehicles: boolean;
  showVehicleHistory: boolean;
  showTracksForVehicleGroupIds: number[];
  showVehicleObservations: boolean;
  showObservationsByObservationGroupIds: number[];
  enableCustomMapLayers: boolean;
  enabledCustomMapLayerIds: number[];
  allowRouteSelfAssign: boolean;
  allowedRouteConfigIds: number[];
  allowReportObservations: boolean;
  observationTypeGroupIds: number[];
  useDvir: boolean;
  preTripFormId: number | undefined;
  postTripFormId: number | undefined;
  defaultBaseMap: string | null;
  defaultHistoryTheme: string | null;
}

@Component({
  selector: 'app-vehicle-group-mobile-settings-edit',
  imports: [
    FormsModule,
    MatDivider,
    MatError,
    ReactiveFormsModule,
    RowWithSelectComponent,
    RowWithToggleComponent,
    CardComponent,
    MatProgressSpinner,
    CardContentDirective,
    MatFormField,
    MatSelect,
    MatOption,
  ],
  templateUrl: './vehicle-group-mobile-settings-edit.component.html',
  styleUrl: './vehicle-group-mobile-settings-edit.component.scss',
})
export class VehicleGroupMobileSettingsEditComponent implements OnInit, OnDestroy {
  isLoading = false;
  isLoadingBaseData = false;

  vehicleGroup: VehicleCategoryModel;

  allInspectionForms: NamedId[];
  allVehicleGroups: NamedId[];
  allObservationTypeGroups: NamedId[];
  allAllowedRouteConfigs: NamedId[];
  allCustomMapLayers: NamedId[];
  allCustomBaseLayers: NamedId[];

  formGroup: FormGroup;
  subscriptions: Subscription[] = [];

  savingCount = 0;

  protected readonly BaseMapType = BaseMapType;
  protected readonly StatusLayerType = StatusLayerType;
  protected readonly String = String;

  constructor(
    private readonly vehicleService: VehiclesService,
    private readonly observationManagementService: ObservationManagementService,
    private readonly inspectionFormsService: InspectionFormsService,
    private readonly routesService: RoutesService,
    private readonly configurationService: ConfigurationService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: FormBuilder
  ) {
    this.createFormGroup();
  }

  ngOnInit() {
    this.loadBaseData().then();
    this.subscriptions.push(
      this.activatedRoute.paramMap.subscribe(params => {
        this.loadData(+params.get('id')).then();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  createFormGroup() {
    this.formGroup = this.fb.group(
      {
        showVehicles: this.fb.control(false),
        showVehicleHistory: this.fb.control(false),
        showTracksForVehicleGroupIds: this.fb.control([]),
        showVehicleObservations: this.fb.control(false),
        showObservationsByObservationGroupIds: this.fb.control([]),
        enableCustomMapLayers: this.fb.control(false),
        enabledCustomMapLayerIds: this.fb.control([]),
        allowRouteSelfAssign: this.fb.control(false),
        allowedRouteConfigIds: this.fb.control([]),
        allowReportObservations: this.fb.control(false),
        observationTypeGroupIds: this.fb.control([]),
        useDvir: this.fb.control(false),
        preTripFormId: this.fb.control(undefined),
        postTripFormId: this.fb.control(undefined),
        defaultBaseMap: this.fb.control(null),
        defaultHistoryTheme: this.fb.control(null),
      },
      {
        validators: [
          validateDvirForms,
          validateObservations,
          validateRoutes,
          validateCustomMapLayers,
          validateVehicleGroups,
          validateObservationsOnShowObservations
        ],
      }
    );

    function validateDvirForms(control: AbstractControl) {
      const { value } = control;
      return value.useDvir && !value.preTripFormId && !value.postTripFormId ? { dvirMissing: true } : null;
    }

    function validateObservations(control: AbstractControl) {
      const { value } = control;
      return value.allowReportObservations && !value.observationTypeGroupIds?.length ? { observationTypeMissing: true } : null;
    }

    function validateRoutes(control: AbstractControl) {
      const { value } = control;
      return value.allowRouteSelfAssign && !value.allowedRouteConfigIds?.length ? { routesMissing: true } : null;
    }

    function validateCustomMapLayers(control: AbstractControl) {
      const { value } = control;
      return value.enableCustomMapLayers && !value.enabledCustomMapLayerIds?.length ? { noCustomMapLayerEnabled: true } : null;
    }

    function validateVehicleGroups(control: AbstractControl) {
      const { value } = control;
      return value.showVehicleHistory && !value.showTracksForVehicleGroupIds?.length ? { vehicleGroupMissing: true } : null;
    }

    function validateObservationsOnShowObservations(control: AbstractControl) {
      const { value } = control;
      return value.showVehicleObservations && !value.showObservationsByObservationGroupIds?.length
        ? { observationTypeOnShowObsMissing: true }
        : null;
    }

    this.subscriptions.push(
      this.formGroup.valueChanges
        .pipe(
          filter((value: Partial<VehicleGroupMobileFormModel>) => {
            if (!value.useDvir && (value.preTripFormId || value.postTripFormId)) {
              this.setFormValue(
                {
                  ...value,
                  preTripFormId: null,
                  postTripFormId: null,
                },
                true
              );
              return false;
            }
            return this.formGroup.valid;
          }),
          debounceTime(500),
          mergeMap(value => {
            const {
              useDvir,
              preTripFormId,
              postTripFormId,

              allowRouteSelfAssign,
              allowedRouteConfigIds,

              observationTypeGroupIds,

              showVehicles,
              showTracksForVehicleGroupIds,
              showObservationsByObservationGroupIds,

              enabledCustomMapLayerIds,
              defaultBaseMap,
              defaultHistoryTheme,
            } = value;
            return this.vehicleService.updateVehicleCategory(this.vehicleGroup.id, {
              ...this.vehicleGroup,
              useDvir,
              preTripFormId,
              postTripFormId,

              allowRouteSelfAssign,
              allowedRouteConfigIds,

              observationTypeGroupIds,

              showVehicles,

              mobileConfiguration: {
                enabledCustomMapLayerIds,
                defaultBaseMap,
                defaultHistoryTheme,
                showTracksForVehicleGroupIds,
                showObservationsByObservationGroupIds,
              },
            });
          })
        )
        .subscribe()
    );
  }

  navigateBack() {
    this.router.navigate([`/${RootRoute.SETTINGS}`, SettingsRoute.MANAGE_VEHICLES, 'group', this.vehicleGroup.id]).then();
  }

  private async loadData(groupId: number) {
    this.isLoading = true;
    this.vehicleGroup = await this.vehicleService.getVehicleCategory(groupId);
    this.setFormValue(this.vehicleGroup);
    this.isLoading = false;
  }

  private async loadBaseData() {
    this.isLoadingBaseData = true;
    Promise.all([
      this.inspectionFormsService.getForms(),
      this.vehicleService.getVehicleCategories(),
      this.observationManagementService.getObservationTypeGroups(),
      this.routesService.getRouteConfigurations(),
      this.configurationService.getAllMapLayers(),
    ])
      .then(responses => {
        const [inspectionFormsResponse, vehicleGroupsResponse, observationGroups, routeConfigsResponse, mapLayersResponse] = responses;
        this.allInspectionForms = inspectionFormsResponse.map(value => new NamedId(value.journalId, value.title));
        this.allVehicleGroups = vehicleGroupsResponse.map(value => new NamedId(value.id, value.name));
        this.allObservationTypeGroups = observationGroups;
        this.allAllowedRouteConfigs = routeConfigsResponse.data.map(value => new NamedId(value.id, value.name));
        this.allCustomMapLayers = mapLayersResponse
          .filter(value => !value.useAsBaseMap)
          .map(value => new NamedId(value.id, value.name));
        this.allCustomBaseLayers = mapLayersResponse
          .filter(value => !!value.useAsBaseMap)
          .map(value => new NamedId(value.id, value.name));
      })
      .finally(() => {
        this.isLoadingBaseData = false;
      });
  }

  private setFormValue(value: VehicleCategoryModel | Partial<VehicleGroupMobileFormModel>, emitEvent = false) {
    // variables present in both models
    const {
      showVehicles,
      allowRouteSelfAssign,
      allowedRouteConfigIds,
      observationTypeGroupIds,
      useDvir,
      preTripFormId,
      postTripFormId,
    } = value;

    // get from form model
    const mobileConfiguration: VehicleGroupMobileConfiguration = value['mobileConfiguration'];
    let defaultBaseMap = value['defaultBaseMap'];
    let defaultHistoryTheme = value['defaultHistoryTheme'];
    let enabledCustomMapLayerIds = value['enabledCustomMapLayerIds'];
    let showTracksForVehicleGroupIds = value['showTracksForVehicleGroupIds'];
    let showObservationsByObservationGroupIds = value['showObservationsByObservationGroupIds'];

    // override from VehicleCategoryModel
    if (mobileConfiguration) {
      enabledCustomMapLayerIds = mobileConfiguration?.enabledCustomMapLayerIds;
      defaultBaseMap = mobileConfiguration?.defaultBaseMap;
      defaultHistoryTheme = mobileConfiguration?.defaultHistoryTheme;
      showTracksForVehicleGroupIds = mobileConfiguration?.showTracksForVehicleGroupIds;
      showObservationsByObservationGroupIds = mobileConfiguration?.showObservationsByObservationGroupIds;
    }
    this.formGroup.setValue(
      {
        showVehicles,
        showVehicleHistory: !!showTracksForVehicleGroupIds && showTracksForVehicleGroupIds.length > 0,
        showTracksForVehicleGroupIds: showTracksForVehicleGroupIds ? [...showTracksForVehicleGroupIds] : [],
        showVehicleObservations: !!showObservationsByObservationGroupIds && showObservationsByObservationGroupIds.length > 0,
        showObservationsByObservationGroupIds: showObservationsByObservationGroupIds ? [...showObservationsByObservationGroupIds] : [],
        enableCustomMapLayers: !!enabledCustomMapLayerIds && enabledCustomMapLayerIds.length > 0,
        enabledCustomMapLayerIds: enabledCustomMapLayerIds ? [...enabledCustomMapLayerIds] : [],
        allowRouteSelfAssign,
        allowedRouteConfigIds: allowedRouteConfigIds ? [...allowedRouteConfigIds] : [],
        allowReportObservations: observationTypeGroupIds?.length > 0,
        observationTypeGroupIds: observationTypeGroupIds ? [...observationTypeGroupIds] : [],
        useDvir,
        preTripFormId: preTripFormId ?? null,
        postTripFormId: postTripFormId ?? null,
        defaultBaseMap: defaultBaseMap ?? null,
        defaultHistoryTheme: defaultHistoryTheme ?? null,
      },
      { emitEvent }
    );
  }

  onVehicleHistoryChange() {
    // if toggled on, check all by default
    if (this.formGroup.controls['showVehicleHistory'].value) {
      this.formGroup.controls['showTracksForVehicleGroupIds'].setValue(
        this.allVehicleGroups.map(v => v.id)
      );
    }
  }

  onVehicleObservationsChange() {
    // if toggled on, check all by default
    if (this.formGroup.controls['showVehicleObservations'].value) {
      this.formGroup.controls['showObservationsByObservationGroupIds'].setValue(
        this.allObservationTypeGroups.map(v => v.id)
      );
    }
  }
}
